/*
 * Copyright 2025-2026, Airspan
 * All Rights Reserved.
 */

import React from "react";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Icon } from "../Icon";
import { IFilterSelectBox, Ioptions } from "./types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";

export interface ActiveFilter {
    key: string;
    label: string;
    fullLabel: string;
    isTruncated: boolean;
    onClear: () => void;
}

interface ActiveFilterChipsProps {
    filters: IFilterSelectBox[];
    resultCount?: number;
    searchKeyword?: string;
    onClearSearch?: () => void;
    onClearAll?: () => void;
    dispatch: (action: any) => void;
}

/** Resolve display text, truncated to maxItems with "..." */
function resolveTruncatedDisplayText(
    filter: IFilterSelectBox,
    value: string[],
    maxItems: number = 5
): { truncated: string; full: string; isTruncated: boolean } {
    const resolved = value.map((v) => resolveSingle(filter, v));
    const full = resolved.join(", ");
    if (resolved.length <= maxItems) {
        return { truncated: full, full, isTruncated: false };
    }
    const truncated = resolved.slice(0, maxItems).join(", ") + ", ...";
    return { truncated, full, isTruncated: true };
}

/** Resolve a stored filter value to its display text */
function resolveDisplayText(
    filter: IFilterSelectBox,
    value: string | string[]
): string {
    if (Array.isArray(value)) {
        return value
            .map((v) => resolveSingle(filter, v))
            .join(", ");
    }
    return resolveSingle(filter, value);
}

function resolveSingle(filter: IFilterSelectBox, value: string): string {
    if (!filter.values || !Array.isArray(filter.values) || filter.values.length === 0) return value;
    const first = filter.values[0];
    if (typeof first === "string") {
        return value; // plain string options — value IS the display text
    }
    // Ioptions — find text by value
    const opt = (filter.values as Ioptions[]).find((o) => String(o.value) === String(value));
    return opt?.text || value;
}

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
    filters,
    resultCount,
    searchKeyword,
    onClearSearch,
    onClearAll,
    dispatch,
}) => {
    const activeFilters: ActiveFilter[] = [];

    for (const filter of filters) {
        const val = filter.value;
        // Skip time-related custom filters (From/To date pickers aren't chip-friendly)
        if (filter.id === "fromEventTime" || filter.id === "toEventTime") continue;

        if (filter.multiple && Array.isArray(val) && val.length > 0) {
            const { truncated, full, isTruncated } = resolveTruncatedDisplayText(filter, val);
            activeFilters.push({
                key: filter.id,
                label: `${filter.label}: ${truncated}`,
                fullLabel: `${filter.label}: ${full}`,
                isTruncated,
                onClear: () => dispatch(filter.setValue([])),
            });
        } else if (!filter.multiple && typeof val === "string" && val !== "") {
            const displayText = resolveDisplayText(filter, val);
            activeFilters.push({
                key: filter.id,
                label: `${filter.label}: ${displayText}`,
                fullLabel: `${filter.label}: ${displayText}`,
                isTruncated: false,
                onClear: () => dispatch(filter.setValue("")),
            });
        } else if (!filter.multiple && typeof val === "number" && val !== 0) {
            const displayText = resolveDisplayText(filter, String(val));
            activeFilters.push({
                key: filter.id,
                label: `${filter.label}: ${displayText}`,
                fullLabel: `${filter.label}: ${displayText}`,
                isTruncated: false,
                onClear: () => dispatch(filter.setValue("")),
            });
        }
    }

    // Search keyword chip
    if (searchKeyword && searchKeyword.trim() && onClearSearch) {
        activeFilters.push({
            key: "__search",
            label: `Search: "${searchKeyword.trim()}"`,
            fullLabel: `Search: "${searchKeyword.trim()}"`,
            isTruncated: false,
            onClear: onClearSearch,
        });
    }

    const hasActive = activeFilters.length > 0;
    if (!hasActive && resultCount === undefined) return null;

    return (
        <div className="flex flex-wrap items-center gap-2 shrink-0 min-w-0 pl-0">
            {resultCount !== undefined && (
                <span className="text-sm text-muted-foreground">
                    {resultCount} {resultCount === 1 ? "result" : "results"}
                </span>
            )}

            {activeFilters.map((f) => {
                const badge = (
                    <Badge key={f.key} variant="secondary" className="gap-1 pr-0.5 pl-2 py-0.5 font-medium text-xs">
                        {f.label}
                        <button
                            type="button"
                            className="h-4 w-4 shrink-0 rounded-sm inline-flex items-center justify-center bg-transparent border-0 p-0 cursor-pointer hover:bg-secondary"
                            onClick={f.onClear}
                            aria-label={`Clear ${f.label}`}
                        >
                            <Icon name="close" size={12} />
                        </button>
                    </Badge>
                );
                return f.isTruncated ? (
                    <TooltipProvider key={f.key} delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex">{badge}</span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs whitespace-normal">
                                {f.fullLabel}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : <React.Fragment key={f.key}>{badge}</React.Fragment>;
            })}

            {hasActive && onClearAll && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground !bg-transparent"
                    onClick={onClearAll}
                >
                    Clear all
                </Button>
            )}
        </div>
    );
};

export default ActiveFilterChips;
